<template>
<section class="px-4 pt-4 pb-8 mx-auto max-w-5xl xl:px-0">
        <!-- Header Section -->
        <div class="mb-8 text-center">
            <h3 class="mb-4 text-2xl leading-none text-center lg:mb-8 sm:text-3xl md:text-32 font-ww-bold xl:px-12" v-html="mainHeading">
            </h3>
            <div class="mx-auto space-y-4">
                <div class="text-base text-left md:text-lg font-ww-regular" v-html="descriptionText1">
                </div>
                <div class="text-base text-left md:text-lg font-ww-regular" v-html="descriptionText2">
                </div>
            </div>
        </div>
        <!-- Main Content Section -->
        <div class="grid grid-cols-1 gap-y-6 gap-x-8 items-center md:grid-cols-11">
            <!-- Supplement Facts Table -->
            <div class="order-2 p-1 bg-white rounded-md border-2 border-black md:col-span-5 md:order-1">
                <h2 class="text-xl tracking-wider uppercase xl:text-3xl md:text-2xl font-ww-bold" v-html="tableTitle">
                </h2>
                <div class="pb-1 text-base border-b-4 border-black md:text-xl font-ww-regular" v-html="servingInfo">
                </div>
                <table class="w-full text-sm">
                    <tbody class="space-y-1">
                        <tr class="text-base border-b border-black xl:text-2xl md:text-xl !leading-none">
                            <th class="py-1 text-left !font-normal">Amount Per Serving</th>
                            <th class="py-1 text-right !font-normal">% DV</th>
                        </tr>
                        <tr v-for="item in nutritionFacts" :key="item.id" 
                            :class="[
                                'text-base xl:text-2xl md:text-xl !leading-none',
                                item.hasBorder ? 'border-b border-black' : ''
                            ]">
                            <td class="py-1" v-html="item.name"></td>
                            <td class="py-1 text-right" v-html="item.value"></td>
                        </tr>
                    </tbody>
                </table>
                <div class="mt-4 text-base border-t-4 border-black xl:text-2xl md:text-xl" v-html="disclaimerText">
                </div>
            </div>
            <!-- Product Image -->
            <div class="flex order-1 justify-center md:col-span-6 md:order-2">
                <div class="">
                   <nuxt-img 
                       :src="productImageSrc" 
                       :sizes="productImageSizes" 
                       :width="productImageWidth" 
                       :height="productImageHeight" 
                       :alt="productImageAlt" 
                       loading="lazy" 
                       class="object-contain w-full h-full" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
defineProps({
  mainHeading: {
    type: String,
    default: 'Just Scoop, Pour, and Stir to Upgrade Your Day.'
  },
  descriptionText1: {
    type: String,
    default: 'Each ingredient in Mindflow is backed by real clinical research showing its ability to improve cognitive performance, focus, memory, or energy—without the downside of overstimulation.'
  },
  descriptionText2: {
    type: String,
    default: 'Here\'s just a glimpse of the science behind Mindflow:'
  },
  tableTitle: {
    type: String,
    default: 'SUPPLEMENT FACTS'
  },
  servingInfo: {
    type: String,
    default: 'Serving Size: 2 (40 Serving Per Bag)'
  },
  nutritionFacts: {
    type: Array,
    default: () => [
      {
        id: 1,
        name: 'Calories',
        value: '14',
        hasBorder: true
      },
      {
        id: 2,
        name: 'Total Sugar',
        value: '1.5 g &nbsp; 5%',
        hasBorder: true
      },
      {
        id: 3,
        name: 'Total Carbohydrates',
        value: '3.5 g &nbsp; 3%',
        hasBorder: true
      },
      {
        id: 4,
        name: 'KSM-66',
        value: '',
        hasBorder: false
      },
      {
        id: 5,
        name: 'Ashwagandha Root Extract',
        value: '250mg &nbsp; *',
        hasBorder: true
      },
      {
        id: 6,
        name: 'Proprietary Mushroom',
        value: '',
        hasBorder: false
      },
      {
        id: 7,
        name: 'Blend Including Lion\'s',
        value: '',
        hasBorder: false
      },
      {
        id: 8,
        name: 'Mane, Turkey Tail,',
        value: '',
        hasBorder: false
      },
      {
        id: 9,
        name: 'Maitake, Reishi, Chaga,',
        value: '',
        hasBorder: false
      },
      {
        id: 10,
        name: 'Shiitake, and Cordyceps',
        value: '2500mg &nbsp; *',
        hasBorder: false
      }
    ]
  },
  disclaimerText: {
    type: String,
    default: '* Daily Value (DV) not established.'
  },
  productImageSrc: {
    type: String,
    default: '/assets/images/mindflow-video.jpg'
  },
  productImageSizes: {
    type: String,
    default: 'xs:283px sm:393px md:504px lg:504px xl:504px'
  },
  productImageWidth: {
    type: Number,
    default: 504
  },
  productImageHeight: {
    type: Number,
    default: 475
  },
  productImageAlt: {
    type: String,
    default: 'Glass with Mindflow supplement powder mixed in liquid'
  }
})

defineBuilderComponent({
    friendlyName: 'Product Info With Visual',
    canHaveChildren: false,
    inputs: [
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: 'Just Scoop, Pour, and Stir to Upgrade Your Day.'
        },
        {
            name: 'descriptionText1',
            type: 'richText',
            defaultValue: 'Each ingredient in Mindflow is backed by real clinical research showing its ability to improve cognitive performance, focus, memory, or energy—without the downside of overstimulation.'
        },
        {
            name: 'descriptionText2',
            type: 'richText',
            defaultValue: 'Here\'s just a glimpse of the science behind Mindflow:'
        },
        {
            name: 'tableTitle',
            type: 'text',
            defaultValue: 'SUPPLEMENT FACTS'
        },
        {
            name: 'servingInfo',
            type: 'richText',
            defaultValue: 'Serving Size: 2 (40 Serving Per Bag)'
        },
        {
            name: 'nutritionFacts',
            type: 'list',
            subFields: [
                {
                    name: 'id',
                    type: 'number',
                    defaultValue: 1
                },
                {
                    name: 'name',
                    type: 'richText',
                    defaultValue: ''
                },
                {
                    name: 'value',
                    type: 'richText',
                    defaultValue: ''
                },
                {
                    name: 'hasBorder',
                    type: 'boolean',
                    defaultValue: false
                }
            ],
            defaultValue: [
                {
                    id: 1,
                    name: 'Calories',
                    value: '14',
                    hasBorder: true
                },
                {
                    id: 2,
                    name: 'Total Sugar',
                    value: '1.5 g &nbsp; 5%',
                    hasBorder: true
                },
                {
                    id: 3,
                    name: 'Total Carbohydrates',
                    value: '3.5 g &nbsp; 3%',
                    hasBorder: true
                },
                {
                    id: 4,
                    name: 'KSM-66',
                    value: '',
                    hasBorder: false
                },
                {
                    id: 5,
                    name: 'Ashwagandha Root Extract',
                    value: '250mg &nbsp; *',
                    hasBorder: true
                },
                {
                    id: 6,
                    name: 'Proprietary Mushroom',
                    value: '',
                    hasBorder: false
                },
                {
                    id: 7,
                    name: 'Blend Including Lion\'s',
                    value: '',
                    hasBorder: false
                },
                {
                    id: 8,
                    name: 'Mane, Turkey Tail,',
                    value: '',
                    hasBorder: false
                },
                {
                    id: 9,
                    name: 'Maitake, Reishi, Chaga,',
                    value: '',
                    hasBorder: false
                },
                {
                    id: 10,
                    name: 'Shiitake, and Cordyceps',
                    value: '2500mg &nbsp; *',
                    hasBorder: false
                }
            ]
        },
        {
            name: 'disclaimerText',
            type: 'richText',
            defaultValue: '* Daily Value (DV) not established.'
        },
        {
            name: 'productImageSrc',
            type: 'file',
            defaultValue: '/assets/images/mindflow-video.jpg'
        },
        {
            name: 'productImageSizes',
            type: 'text',
            defaultValue: 'xs:283px sm:393px md:504px lg:504px xl:504px'
        },
        {
            name: 'productImageWidth',
            type: 'number',
            defaultValue: 504
        },
        {
            name: 'productImageHeight',
            type: 'number',
            defaultValue: 475
        },
        {
            name: 'productImageAlt',
            type: 'text',
            defaultValue: 'Glass with Mindflow supplement powder mixed in liquid'
        }
    ]
})
</script>